import './style/products.scss';
import {AwesomeIcon, Link, Loader} from '@dvrd/dvr-controls';
import React, {ReactNode, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import BolProduct from '../../models/bolProductModel';
import {ProductGroup, ProductStatus, ProductVisibility} from '../../util/productsUtil';
import {
    BluemateButton,
    BluemateEmptyState,
    BluemateListGroup,
    BluemateSearchInput,
    BluemateSegmentedToggle,
    BluemateTooltip
} from '../controls';
import {DotsLoader} from '../controls/loader/dotsLoader';
import SpinnerIcon from '../icon/spinnerIcon';
import InfoBanner from '../misc/infoBanner';
import ProductRow from './productRow/productRow';

interface Props {
    onChangeSearch: (value: string) => void;
    onSearch: VoidFunction;
    onChangeVisibility: (value: ProductVisibility) => void;
    onEditName: (product: BolProduct) => void;
    onEditVat: (product: BolProduct) => void;
    onEditCosts: (product: BolProduct) => void;
    onToggleHidden: (product: BolProduct) => void;
    onClickDownload: (product: BolProduct) => void;
    onClickDownloadAllQrCodes: VoidFunction;
    onClickSync: VoidFunction;
    onRowVisible: (ix: number, product: BolProduct) => void;
    onClickDefaultVat: VoidFunction;
    onClickIntro: VoidFunction;
    onClickImportExport: VoidFunction;
    canImportExport: boolean;
    groups: ProductGroup[];
    statusByEan: Record<string, ProductStatus>;
    costs: Record<string, number | null>;
    search: string;
    appliedSearch: string;
    visibility: ProductVisibility;
    loadingProducts: boolean;
    loadingMore: boolean;
    downloadingIds: Set<string>;
    downloadingAll: boolean;
    canDownloadAll: boolean;
    updatingIds: Set<string>;
    canEditProduct: boolean;
    showSyncButton: boolean;
    syncing: boolean;
    importing: boolean;
    importingCount: number | null;
    hasBolConnection: boolean;
}

export default function Products(props: Props) {
    const {t} = useTranslation();
    const [closedBanners, setClosedBanners] = useState<Set<number>>(new Set());
    const {
        onChangeSearch,
        onSearch,
        onChangeVisibility,
        onEditName,
        onEditVat,
        onEditCosts,
        onToggleHidden,
        onClickDownload,
        onClickDownloadAllQrCodes,
        onClickSync,
        onRowVisible,
        onClickDefaultVat,
        onClickIntro,
        onClickImportExport,
        canImportExport,
        groups,
        statusByEan,
        costs,
        search,
        appliedSearch,
        visibility,
        loadingProducts,
        loadingMore,
        downloadingIds,
        downloadingAll,
        canDownloadAll,
        updatingIds,
        canEditProduct,
        showSyncButton,
        syncing,
        importing,
        importingCount,
        hasBolConnection
    } = props;

    function onCloseBanner(index: number) {
        return function () {
            setClosedBanners(current => new Set(current).add(index));
        };
    }

    function getSyncLabel() {
        if (!importing) return t('products.sync.button', 'Sync met Bol');
        if (importingCount)
            return t('products.sync.fetchingCount', 'Bezig met ophalen van {{count}} producten', {
                count: importingCount
            });
        return t('products.sync.fetching', 'Bezig met ophalen van producten');
    }

    function getGroupTitle(group: ProductGroup) {
        if (group.key === 'all') return t('products.group.all', 'Alle producten');
        if (group.key === 'hidden') return t('products.group.hidden', 'Verborgen producten');
        return t('products.group.visible', 'Zichtbare producten');
    }

    function renderIntroBannerText() {
        return (
            <>
                {t('products.subtitle', 'Beheer hier jouw producten: naam, BTW, kosten, zichtbaarheid en QR-codes.')}
                <br />
                <br />
                <Trans
                    i18nKey='products.subtitleReviews'
                    defaults='Wil je meer reviews verzamelen?<0/>Voeg fysieke QR codes toe in jouw verpakking.<0/>Deze leiden een klant direct naar de review pagina van het product op bol. Bekijk een <1>voorbeeld</1>.'
                    components={[<br key='0' />, <Link key='1' onClick={onClickIntro} />]}
                />
            </>
        );
    }

    function renderBanners() {
        const banners: Array<{content: ReactNode; variant: 'info' | 'warning'}> = [
            {
                content: renderIntroBannerText(),
                variant: 'info'
            },
            {
                content: t(
                    'products.bolWarning',
                    'Alle wijzigingen die je hier maakt, gelden alleen voor Bluemate, niet voor bol zelf.'
                ),
                variant: 'warning'
            }
        ];
        if (banners.every((_, index) => closedBanners.has(index))) return null;
        return (
            <div className='page-banners'>
                {banners.map((banner, index) =>
                    closedBanners.has(index) ? null : (
                        <InfoBanner key={index} variant={banner.variant} onClose={onCloseBanner(index)}>
                            {banner.content}
                        </InfoBanner>
                    )
                )}
            </div>
        );
    }

    function renderFilterContainer() {
        return (
            <div className='search-container'>
                <div className='left'>
                    <BluemateSearchInput
                        onChange={onChangeSearch}
                        value={search}
                        onEnter={onSearch}
                        className='search-field'
                        placeholder={t('products.searchPlaceholder', 'Zoek op EAN of titel')}
                    />
                    <BluemateSegmentedToggle<ProductVisibility>
                        options={[
                            {value: 'all', label: t('products.filter.all', 'Alle')},
                            {value: 'hidden', label: t('products.filter.hidden', 'Verborgen')}
                        ]}
                        value={visibility}
                        onChange={onChangeVisibility}
                    />
                    <BluemateButton
                        onClick={onClickDefaultVat}
                        label={t('products.defaultVat.title', 'Standaard BTW')}
                        ornaments={{element: <AwesomeIcon name='percent' />}}
                        tertiary
                        shape='square'
                        align='center'
                    />
                    {canImportExport && (
                        <BluemateButton
                            onClick={onClickImportExport}
                            label={t('products.importExport.label', 'Import & Export...')}
                            ornaments={{element: <AwesomeIcon name='file-excel' prefix='far' />}}
                            tertiary
                            shape='square'
                            align='center'
                        />
                    )}
                </div>
                <div className='right'>
                    <BluemateButton
                        onClick={onClickDownloadAllQrCodes}
                        label={t('products.downloadAll.label', 'Alle QR')}
                        disabled={downloadingAll || !canDownloadAll}
                        ornaments={{
                            element: downloadingAll ? <SpinnerIcon /> : <AwesomeIcon name='download' />
                        }}
                        tertiary
                        shape='square'
                        align='center'
                    />
                    <BluemateTooltip text={t('products.intro.qrInfoTooltip', 'Zo werken de QR-codes')}>
                        <BluemateButton
                            onClick={onClickIntro}
                            label={<AwesomeIcon name='info-circle' />}
                            ornaments={{element: <AwesomeIcon name='qrcode' />}}
                            className='qr-info-button'
                            tertiary
                            shape='square'
                            align='center'
                        />
                    </BluemateTooltip>
                    {showSyncButton && (
                        <BluemateButton
                            onClick={onClickSync}
                            label={getSyncLabel()}
                            disabled={syncing || !hasBolConnection}
                            ornaments={{element: <AwesomeIcon name='rotate' className={syncing ? 'spin' : ''} />}}
                            tertiary
                            shape='square'
                            align='center'
                        />
                    )}
                </div>
            </div>
        );
    }

    function renderProducts() {
        return (
            <div className='products-list-container'>
                <Loader active={loadingProducts} />
                {renderProductRows()}
                {loadingMore && (
                    <div className='load-more-container'>
                        <DotsLoader dotSize='3rem' />
                    </div>
                )}
            </div>
        );
    }

    function renderProductRows() {
        if (groups.length) return groups.map(renderGroup);
        if (loadingProducts) return null;
        if (appliedSearch.trim().length || visibility !== 'all') return renderProductsNotFound();
        return renderEmptyProducts();
    }

    function renderGroup(group: ProductGroup) {
        return (
            <BluemateListGroup key={group.key} className='products-list-group' title={getGroupTitle(group)}>
                <div className='products-list'>{group.items.map(item => renderProduct(item.product, item.index))}</div>
            </BluemateListGroup>
        );
    }

    function renderProduct(product: BolProduct, ix: number) {
        return (
            <ProductRow
                key={product.id}
                product={product}
                index={ix}
                status={statusByEan[product.ean] ?? 'visible'}
                costs={costs[product.ean]}
                downloading={downloadingIds.has(product.id)}
                updating={updatingIds.has(product.id)}
                canEdit={canEditProduct}
                onEditName={onEditName}
                onEditVat={onEditVat}
                onEditCosts={onEditCosts}
                onToggleHidden={onToggleHidden}
                onClickDownload={onClickDownload}
                onVisible={onRowVisible}
            />
        );
    }

    function renderProductsNotFound() {
        return (
            <BluemateEmptyState image='data-rows' title={t('products.notFound.title', 'Geen producten gevonden')}>
                {t('products.notFound.subtitle', 'Probeer een andere zoekterm of een ander filter.')}
            </BluemateEmptyState>
        );
    }

    function renderEmptyProducts() {
        return (
            <BluemateEmptyState image='data-rows' title={t('products.empty.title', 'Nog geen producten beschikbaar')}>
                {hasBolConnection
                    ? t(
                          'products.empty.subtitle',
                          'Zodra je producten met bol zijn gesynchroniseerd, verschijnen ze hier.'
                      )
                    : t('products.empty.connect', 'Maak eerst verbinding met bol om je producten te zien.')}
            </BluemateEmptyState>
        );
    }

    return (
        <div className='products-page'>
            <div className='filter-container'>
                <div className='title-row'>
                    <label className='col-title'>{t('products.title', 'Producten')}</label>
                </div>
                {renderBanners()}
                {renderFilterContainer()}
            </div>
            {renderProducts()}
        </div>
    );
}
